import { createColumnHelper } from "@tanstack/react-table";
import { CONDITION_OPTIONS } from "../BookingForm/constants";
import { type SectionId, type MetadataField } from "../BookingForm/types";
import { BOOKINGS_SORT_FIELDS, type BookingsSortField } from "./constants";
import { type SupplyChainBooking } from "./index";
import { Badge } from "@/components/ui/badge";
import { toDisplayDate } from "@/lib/utils/dates";
import { formatEmptyableValue, formatEnumLabel, formatMetricValue, formatName } from "@/lib/utils/formatting";

const { accessor } = createColumnHelper<SupplyChainBooking>();

export const isSortable = (field: BookingsSortField) => {
  return BOOKINGS_SORT_FIELDS.includes(field);
};

/**
 * All metadata column definitions for the bookings table.
 * Each column includes its field ID (matching MetadataField) for visibility filtering.
 * If a MetadataField id value does not directly match a BookingsSortField, we need to use the BookingsSortField id value in the id
 * and pass the MetadataField id in the meta.fieldId property so that sorting can work.
 */
export const metadataColumns = [
  // Lot & Inventory Details
  accessor((row) => row?.lotNumber, {
    id: "LOT_NUMBER" satisfies MetadataField,
    header: "Lot Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("LOT_NUMBER"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.penNumber, {
    id: "PEN_NUMBER" satisfies MetadataField,
    header: "Pen Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("PEN_NUMBER"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.accountNumber, {
    id: "ACCOUNT_NUMBER" satisfies MetadataField,
    header: "Contract Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("ACCOUNT_NUMBER"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.sexClass, {
    id: "SEX_CLASS" satisfies MetadataField,
    header: "Sex Class",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatEnumLabel(value) : "\u2014";
    },
    enableSorting: isSortable("SEX_CLASS"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.breedNote, {
    id: "BREED_NOTE" satisfies MetadataField,
    header: "Breed Note",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("BREED_NOTE"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.accreditations, {
    id: "ACCREDITATIONS",
    header: "Accreditations",
    cell: (info) => {
      const accreditations = info.getValue();
      if (!accreditations || accreditations.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {accreditations.map((acc) => (
            <Badge
              key={acc.id}
              variant="secondary"
              className="rounded-full gap-1 h-8 px-4 font-medium border-none bg-blue-300/30 text-blue-700"
            >
              {acc.label}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.confirmed, {
    id: "CONFIRMED" satisfies MetadataField,
    header: "Status",
    cell: (info) => {
      const value = info.getValue();
      return value === true ? "Confirmed" : value === false ? "Unconfirmed" : "\u2014";
    },
    enableSorting: isSortable("CONFIRMED"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.labels, {
    id: "LABELS",
    header: "Labels",
    cell: (info) => {
      const labels = info.getValue();
      if (!labels || labels.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {labels.map((label) => (
            <Badge
              key={label.id}
              variant="secondary"
              className="rounded-full gap-1 h-8 px-4 font-medium border-none bg-green-300/30 text-green-700"
            >
              {label.label}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.condition, {
    id: "CONDITION" satisfies MetadataField,
    header: "Condition",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatEnumLabel(value) : "\u2014";
    },
    enableSorting: isSortable("CONDITION"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.conditions, {
    id: "CONDITIONS",
    header: "Conditions",
    cell: (info) => {
      const conditions = info.getValue();
      if (!conditions || conditions.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {conditions.map((condition) => {
            const conditionOption = CONDITION_OPTIONS.find((opt) => opt.value === condition);
            const label = conditionOption?.label ?? condition;
            return (
              <Badge
                key={condition}
                variant="secondary"
                className="rounded-full gap-1 h-8 px-4 font-medium border-none bg-slate-300/30 text-slate-700"
              >
                {label}
              </Badge>
            );
          })}
        </div>
      );
    },
    enableSorting: false,
    meta: { group: "lot_inventory" satisfies SectionId },
  }),
  accessor((row) => row?.ownershipType, {
    id: "OWNERSHIP_TYPE" satisfies MetadataField,
    header: "Ownership Type",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatEnumLabel(value) : "\u2014";
    },
    enableSorting: isSortable("OWNERSHIP_TYPE"),
    meta: { group: "lot_inventory" satisfies SectionId },
  }),

  // Dates & Scheduling
  accessor((row) => row?.dateOfSale, {
    id: "DATE_OF_SALE" satisfies MetadataField,
    header: "Date of Sale",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: isSortable("DATE_OF_SALE"),
    meta: { group: "dates" satisfies SectionId },
  }),
  accessor((row) => row?.dateOfVisit, {
    id: "DATE_OF_VISIT" satisfies MetadataField,
    header: "Date of Visit",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: isSortable("DATE_OF_VISIT"),
    meta: { group: "dates" satisfies SectionId },
  }),
  accessor((row) => row?.projectedOutDate, {
    id: "PROJECTED_OUT_DATE" satisfies MetadataField,
    header: "Projected Out Date",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: isSortable("PROJECTED_OUT_DATE"),
    meta: { group: "dates" satisfies SectionId },
  }),
  accessor((row) => row?.dateDelivered, {
    id: "DATE_DELIVERED" satisfies MetadataField,
    header: "Date Delivered",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: isSortable("DATE_DELIVERED"),
    meta: { group: "dates" satisfies SectionId },
  }),

  // Financial Details
  accessor((row) => row?.price, {
    id: "PRICE" satisfies MetadataField,
    header: "Price",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: isSortable("PRICE"),
    meta: { group: "financial" satisfies SectionId },
  }),
  accessor((row) => row?.freightCost, {
    id: "FREIGHT_COST" satisfies MetadataField,
    header: "Freight Cost",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: isSortable("FREIGHT_COST"),
    meta: { group: "financial" satisfies SectionId },
  }),
  accessor((row) => row?.slide, {
    id: "SLIDE" satisfies MetadataField,
    header: "Slide",
    cell: (info) =>
      formatMetricValue({ value: String(info.getValue() ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: isSortable("SLIDE"),
    meta: { group: "financial" satisfies SectionId },
  }),
  accessor((row) => row?.purchasePricePerCwt, {
    id: "PURCHASE_PRICE_PER_CWT" satisfies MetadataField,
    header: "Purchase Price/Cwt",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: isSortable("PURCHASE_PRICE_PER_CWT"),
    meta: { group: "financial" satisfies SectionId },
  }),
  accessor((row) => row?.riskManagement, {
    id: "RISK_MANAGEMENT" satisfies MetadataField,
    header: "Risk Management",
    cell: (info) => {
      const value = info.getValue();
      return value === true ? "Yes" : value === false ? "No" : "\u2014";
    },
    enableSorting: isSortable("RISK_MANAGEMENT"),
    meta: { group: "financial" satisfies SectionId },
  }),

  // Weight & Quality Information
  accessor((row) => row?.shippedWeight, {
    id: "SHIPPED_WEIGHT" satisfies MetadataField,
    header: "Shipped Weight",
    cell: (info) =>
      formatMetricValue({ value: String(info.getValue() ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: isSortable("SHIPPED_WEIGHT"),
    meta: { group: "weight" satisfies SectionId },
  }),
  accessor((row) => row?.baseWeight, {
    id: "BASE_WEIGHT" satisfies MetadataField,
    header: "Base Weight",
    cell: (info) =>
      formatMetricValue({ value: String(info.getValue() ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: isSortable("BASE_WEIGHT"),
    meta: { group: "weight" satisfies SectionId },
  }),
  accessor((row) => row?.estimatedChoicePercent, {
    id: "ESTIMATED_CHOICE_PERCENT" satisfies BookingsSortField | MetadataField,
    header: "Choice %",
    cell: (info) => {
      const value = info.getValue();
      return formatMetricValue({
        value: String(value ? value / 100 : ""),
        fallbackLabel: "\u2014",
        metricType: "percentage",
      });
    },
    enableSorting: isSortable("ESTIMATED_CHOICE_PERCENT"),
    meta: { group: "weight" satisfies SectionId },
  }),
  accessor((row) => row?.shrinkPercent, {
    id: "SHRINK_PERCENT" satisfies MetadataField,
    header: "Shrink %",
    cell: (info) => {
      const value = info.getValue();
      return formatMetricValue({
        value: String(value ? value / 100 : ""),
        fallbackLabel: "\u2014",
        metricType: "percentage",
      });
    },
    enableSorting: isSortable("SHRINK_PERCENT"),
    meta: { group: "weight" satisfies SectionId },
  }),
  accessor((row) => row?.estimatedWeightNote, {
    id: "ESTIMATED_WEIGHT_NOTE" satisfies MetadataField,
    header: "Est. Weight Note",
    cell: (info) => {
      const value = info.getValue();
      return value ? <div>{value}</div> : "—";
    },
    enableSorting: isSortable("ESTIMATED_WEIGHT_NOTE"),
    meta: { group: "weight" satisfies SectionId },
  }),

  // Source & Documentation
  accessor((row) => row?.sourceNote, {
    id: "SOURCE_NOTE" satisfies MetadataField,
    header: "Source Note",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("SOURCE_NOTE"),
    meta: { group: "source" satisfies SectionId },
  }),
  accessor((row) => row?.brandNote, {
    id: "BRAND_NOTE" satisfies MetadataField,
    header: "Brand Note",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("BRAND_NOTE"),
    meta: { group: "source" satisfies SectionId },
  }),
  accessor((row) => row?.certificateNumber, {
    id: "CERTIFICATE_NUMBER" satisfies MetadataField,
    header: "Certificate Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: isSortable("CERTIFICATE_NUMBER"),
    meta: { group: "source" satisfies SectionId },
  }),
  accessor((row) => row?.program, {
    id: "PROGRAM_LABEL",
    header: "Program",
    cell: (info) => formatEmptyableValue(info.getValue()?.label),
    enableSorting: isSortable("PROGRAM_LABEL"),
    meta: {
      group: "source" satisfies SectionId,
      fieldId: "PROGRAM" as MetadataField,
    },
  }),
  accessor((row) => row?.grid, {
    id: "GRID_LABEL",
    header: "Grid",
    cell: (info) => formatEmptyableValue(info.getValue()?.label),
    enableSorting: isSortable("GRID_LABEL"),
    meta: {
      group: "source" satisfies SectionId,
      fieldId: "GRID" as MetadataField,
    },
  }),

  // Transportation & Services
  accessor((row) => row?.truckingCompany, {
    id: "TRUCKING_COMPANY" satisfies MetadataField,
    header: "Trucking Company",
    cell: (info) => formatEmptyableValue(info.getValue()?.businessName),
    enableSorting: isSortable("TRUCKING_COMPANY"),
    meta: { group: "transportation" satisfies SectionId },
  }),
  accessor((row) => row?.broker, {
    id: "BROKER_BUSINESS_NAME",
    header: "Broker",
    cell: (info) => formatEmptyableValue(info.getValue()?.businessName),
    enableSorting: isSortable("BROKER_BUSINESS_NAME"),
    meta: {
      group: "transportation" satisfies SectionId,
      fieldId: "BROKER" as MetadataField,
    },
  }),
  accessor((row) => row?.veterinarian, {
    id: "VETERINARIAN_LAST_NAME",
    header: "Veterinarian",
    cell: (info) => {
      const vet = info.getValue();
      return vet?.user ? formatName({ firstName: vet.user.firstName, lastName: vet.user.lastName }) : "\u2014";
    },
    enableSorting: isSortable("VETERINARIAN_LAST_NAME"),
    meta: {
      group: "transportation" satisfies SectionId,
      fieldId: "VETERINARIAN" as MetadataField,
    },
  }),
  accessor((row) => row?.serviceProvided, {
    id: "SERVICE_PROVIDED" satisfies MetadataField,
    header: "Service Provided",
    cell: (info) => {
      const value = info.getValue();
      return value ? <div className="whitespace-pre-wrap break-words">{value}</div> : "—";
    },
    enableSorting: isSortable("SERVICE_PROVIDED"),
    meta: { group: "transportation" satisfies SectionId },
  }),
];
